import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { buildPathWithProjectName, getProjectNameFromSearch } from '../../utils/projectName';
import { useActiveProject } from '../../hooks/useActiveProject';
import { shouldShowReportsSection, resolveInferenceAccess, resolveCdpAccess, LOCKED_SECTION_TOOLTIP } from '../../utils/authPermissions';
import { useAuth } from '../../hooks/useAuth';
import './ListStyles.css';

// Декларативная структура бокового меню.
// Типы узлов:
//   link        — обычный пункт (to, опц. icon, match(pathname), withProject)
//   placeholder — некликабельный пункт (раздела пока нет в проекте)
//   group       — раскрывающийся блок с детьми (children: link | placeholder)
//   section     — статичный заголовок-раздел (опц. show(perms) или access(perms) для гейтинга)
const NAV = [
  {
    type: 'link',
    label: 'Проекты',
    to: '/projects',
    icon: 'library.svg',
    withProject: true,
    match: (p) => p.startsWith('/projects') && !p.includes('/cdp/'),
  },
  {
    type: 'link',
    label: 'Главная',
    to: '/',
    icon: 'home.svg',
    match: (p) => p === '/',
  },

  {
    type: 'section',
    title: 'Inference',
    access: (perms) => resolveInferenceAccess(perms),
    items: [
      {
        type: 'group',
        id: 'ready-models',
        label: 'Готовые модели',
        children: [
          {
            label: 'Библиотека моделей',
            to: '/model-library',
            match: (p) => p === '/model-library' || p.startsWith('/model/'),
          },
          { label: 'Песочница', to: '/sandbox' },
          { label: 'Использование', to: '/usage' },
        ],
      },
      {
        type: 'group',
        id: 'recsys',
        label: 'Рекомендательные системы',
        children: [
          {
            label: 'Кампании',
            to: '/rec/campaigns',
            withProject: true,
            match: (p) => p.startsWith('/rec/campaigns'),
          },
          {
            label: 'Модели',
            to: '/rec/models',
            withProject: true,
            match: (p) => p.startsWith('/rec/models'),
          },
          {
            label: 'Места показа',
            to: '/rec/placements',
            withProject: true,
            match: (p) => p.startsWith('/rec/placements'),
          },
          { label: 'Отчёты', placeholder: true },
        ],
      },
      {
        type: 'group',
        id: 'rag',
        label: 'RAG',
        children: [
          {
            label: 'Модули',
            to: '/rag',
            withProject: true,
            match: (p) => p === '/rag' || /\/projects\/[^/]+\/rags\/[^/]+$/.test(p),
          },
          {
            label: 'Конфигурация',
            to: '/rag/config',
            withProject: true,
            match: (p) => p.includes('/rag/config') || (p.includes('/rags/') && p.endsWith('/config')),
          },
          {
            label: 'Чат',
            to: '/rag/chat',
            withProject: true,
            match: (p) => p.includes('/rag/chat') || (p.includes('/rags/') && p.endsWith('/chat')),
          },
          {
            label: 'API и интеграции',
            to: '/rag/output',
            withProject: true,
            match: (p) => p.includes('/rag/output') || (p.includes('/rags/') && p.includes('/output')),
          },
        ],
      },
      {
        type: 'link',
        label: 'MCP',
        to: '/mcp',
        withProject: true,
        match: (p) => p === '/mcp',
      },
    ],
  },

  {
    type: 'section',
    title: 'Data core',
    items: [
      {
        type: 'group',
        id: 'storage',
        label: 'Хранилище',
        children: [
          {
            label: 'Содержимое БД',
            to: '/storage',
            withProject: true,
            match: (p) => p === '/storage' || p.startsWith('/storage/'),
          },
          {
            label: 'Мета Таблиц',
            to: '/feature-store/tables',
            withProject: true,
            match: (p) => p.startsWith('/feature-store/tables'),
          },
          {
            label: 'Датасеты',
            to: '/datasets',
            withProject: true,
            match: (p) => p === '/datasets' || p.startsWith('/datasets/'),
          },
        ],
      },
      {
        type: 'group',
        id: 'feature-store',
        label: 'Feature store',
        children: [
          {
            label: 'События',
            to: '/feature-store/events',
            withProject: true,
            match: (p) => p.startsWith('/feature-store/events'),
          },
          {
            label: 'Параметры',
            to: '/feature-store/params',
            withProject: true,
            match: (p) => p.startsWith('/feature-store/params'),
          },
          {
            label: 'Значения в данных',
            to: '/feature-store/values',
            withProject: true,
            match: (p) => p.startsWith('/feature-store/values'),
          },
          {
            label: 'Фичи стор',
            to: '/feature-store/features',
            withProject: true,
            match: (p) => p.startsWith('/feature-store/features'),
          },
          {
            label: 'Мониторинг качества',
            to: '/feature-store/monitoring',
            withProject: true,
            match: (p) => p.startsWith('/feature-store/monitoring'),
          },
          {
            label: 'Шаблонизатор',
            to: '/feature-store/templates',
            withProject: true,
            match: (p) => p.startsWith('/feature-store/templates'),
          },
          { label: 'Импорт', placeholder: true },
        ],
      },
      {
        type: 'group',
        id: 'integrations',
        label: 'Интеграции',
        children: [
          {
            label: 'Цепочки',
            to: '/chains',
            withProject: true,
            match: (p) => p === '/chains' || p.startsWith('/chains/'),
          },
          {
            label: 'Интеграции',
            to: '/integrations',
            withProject: true,
            match: (p) => p === '/integrations' || p.startsWith('/integrations/'),
          },
          {
            label: 'Подключения',
            to: '/connections',
            withProject: true,
            match: (p) => p === '/connections' || p.startsWith('/connections/'),
          },
          {
            label: 'Задачи',
            to: '/tasks',
            withProject: true,
            match: (p) => p === '/tasks' || p.startsWith('/tasks/'),
          },
        ],
      },
    ],
  },

  {
    type: 'section',
    title: 'Отчёты',
    show: (perms) => shouldShowReportsSection(perms.canViewReports),
    items: [
      {
        type: 'link',
        label: 'Аудитория',
        to: '/reporting/audience',
        withProject: true,
        match: (p) => p.endsWith('/reporting/audience'),
      },
      {
        type: 'link',
        label: 'Воронки',
        to: '/reporting/funnels',
        withProject: true,
        match: (p) => p.endsWith('/reporting/funnels'),
      },
      {
        type: 'link',
        label: 'Ретеншен',
        to: '/reporting/retention',
        withProject: true,
        match: (p) => p.endsWith('/reporting/retention'),
      },
    ],
  },

  {
    type: 'section',
    title: 'CDP',
    access: (perms) => resolveCdpAccess(perms),
    items: [
      {
        type: 'link',
        label: 'Профили',
        to: '/cdp/profiles',
        withProject: true,
        match: (p) => p.startsWith('/cdp/profiles'),
      },
      { type: 'placeholder', label: 'Сегментация' },
      {
        type: 'link',
        label: 'Конфиги',
        to: '/cdp/configs',
        withProject: true,
        match: (p) => p.includes('/cdp/configs'),
      },
    ],
  },
];

// Плоский список всех раскрывающихся блоков (для авто-раскрытия по активному маршруту)
const ALL_GROUPS = [];
NAV.forEach((node) => {
  if (node.type === 'section') {
    node.items.forEach((item) => {
      if (item.type === 'group') ALL_GROUPS.push(item);
    });
  } else if (node.type === 'group') {
    ALL_GROUPS.push(node);
  }
});

const isLinkActive = (item, pathname) => {
  if (!item || item.placeholder || item.type === 'placeholder') return false;
  if (typeof item.match === 'function') return item.match(pathname);
  if (!item.to) return false;
  return pathname === item.to.split('?')[0];
};

const groupHasActiveChild = (group, pathname) =>
  group.children.some((child) => isLinkActive(child, pathname));

const Chevron = ({ open }) => (
  <svg
    className={`menu-group-chevron ${open ? 'open' : ''}`}
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="9 6 15 12 9 18" />
  </svg>
);

const LockIcon = () => (
  <img
    className="menu-icon-lock"
    src={process.env.PUBLIC_URL + '/icons/lock.svg'}
    alt=""
    aria-hidden="true"
  />
);

const Menu = ({ open = false }) => {
  const location = useLocation();
  const { canViewReports, canViewInference, canViewCdp, isAdmin } = useAuth();
  const perms = { canViewReports, canViewInference, canViewCdp, isAdmin };

  const { effectiveDbName } = useActiveProject();
  const projectName = getProjectNameFromSearch(location.search) || effectiveDbName;
  const withProjectName = (path) => buildPathWithProjectName(path, projectName);

  const [openGroups, setOpenGroups] = useState(() => new Set());

  // Если пользователь перешёл в подпункт — гарантированно раскрываем его блок
  useEffect(() => {
    setOpenGroups((prev) => {
      let changed = false;
      const next = new Set(prev);
      ALL_GROUPS.forEach((group) => {
        if (groupHasActiveChild(group, location.pathname) && !next.has(group.id)) {
          next.add(group.id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [location.pathname]);

  const toggleGroup = (id) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isGroupOpen = (group) =>
    openGroups.has(group.id) || groupHasActiveChild(group, location.pathname);

  const renderLink = (item, key, isSub = false, locked = false) => {
    if (locked) {
      const className = ['menu-link', 'locked', isSub ? 'menu-sublink' : '']
        .filter(Boolean)
        .join(' ');
      return (
        <span
          key={key}
          className={className}
          aria-disabled="true"
          title={LOCKED_SECTION_TOOLTIP}
          aria-label={`${item.label}. ${LOCKED_SECTION_TOOLTIP}`}
        >
          {item.icon && (
            <img
              className="menu-icon"
              src={process.env.PUBLIC_URL + '/icons/' + item.icon}
              alt=""
            />
          )}
          <span className="menu-link-label">{item.label}</span>
        </span>
      );
    }

    const to = item.withProject ? withProjectName(item.to) : item.to;
    const active = isLinkActive(item, location.pathname);
    const className = [
      'menu-link',
      isSub ? 'menu-sublink' : '',
      active ? 'active' : '',
    ]
      .filter(Boolean)
      .join(' ');
    return (
      <NavLink key={key} className={className} to={to}>
        {item.icon && (
          <img
            className="menu-icon"
            src={process.env.PUBLIC_URL + '/icons/' + item.icon}
            alt=""
          />
        )}
        {item.label}
      </NavLink>
    );
  };

  const renderPlaceholder = (item, key, isSub = false) => {
    const className = ['menu-link', 'disabled', isSub ? 'menu-sublink' : '']
      .filter(Boolean)
      .join(' ');
    return (
      <span key={key} className={className} aria-disabled="true">
        {item.icon && (
          <img
            className="menu-icon"
            src={process.env.PUBLIC_URL + '/icons/' + item.icon}
            alt=""
          />
        )}
        {item.label}
      </span>
    );
  };

  const renderGroup = (group, locked = false) => {
    const open = !locked && isGroupOpen(group);
    return (
      <div className={`menu-group${locked ? ' locked' : ''}`} key={group.id}>
        {locked ? (
          <div
            className="menu-group-header locked"
            title={LOCKED_SECTION_TOOLTIP}
            aria-label={`${group.label}. ${LOCKED_SECTION_TOOLTIP}`}
          >
            <span className="menu-group-label">{group.label}</span>
          </div>
        ) : (
          <button
            type="button"
            className="menu-group-header"
            onClick={() => toggleGroup(group.id)}
            aria-expanded={open}
          >
            <span className="menu-group-label">{group.label}</span>
            <Chevron open={open} />
          </button>
        )}
        {open && (
          <div className="menu-group-children">
            {group.children.map((child, i) =>
              child.placeholder
                ? renderPlaceholder(child, i, true)
                : renderLink(child, i, true, locked)
            )}
          </div>
        )}
      </div>
    );
  };

  const renderItem = (item, key, locked = false) => {
    if (item.type === 'group') return renderGroup(item, locked);
    if (item.type === 'placeholder' || item.placeholder)
      return renderPlaceholder(item, key, false);
    return renderLink(item, key, false, locked);
  };

  const topLinks = NAV.filter((node) => node.type === 'link');
  const sections = NAV.filter((node) => node.type === 'section');

  return (
    <div className={`menu-container ${open ? 'is-open' : ''}`.trim()}>
      <div className="menu-logo">
        <span className="menu-logo-text">Inference</span>
      </div>

      <div className="menu-section">
        {topLinks.map((item, i) => renderItem(item, `top-${i}`))}
      </div>

      {sections.map((section) => {
        const state = section.access
          ? section.access(perms)
          : section.show
            ? (section.show(perms) ? 'visible' : 'hidden')
            : 'visible';
        if (state === 'hidden') return null;
        const locked = state === 'locked';
        return (
          <div className={`menu-section${locked ? ' menu-section-locked' : ''}`} key={section.title}>
            <div className="menu-section-title">
              {section.title}
              {locked && <LockIcon />}
            </div>
            {section.items.map((item, i) => renderItem(item, `${section.title}-${i}`, locked))}
          </div>
        );
      })}

      <div className="menu-section documentation-section">
        <NavLink className="menu-link" to="/docs" target="_blank" rel="noopener noreferrer">
          <img className="menu-icon" src={process.env.PUBLIC_URL + '/icons/docs.svg'} alt="" />
          Документация
        </NavLink>
      </div>
    </div>
  );
};

export default Menu;
